import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { UniversalLink } from '@plone/volto/components';
import {
  datasDaEtapa,
  estadosDasEtapas,
  folha,
  horaLegivel,
  periodo,
  situacao,
} from './situacao';

/**
 * Prazo de edital — datas em folhas de calendário, etiqueta e situação na cor
 * do momento (em breve azul, aberto verde, último dia vermelho, encerrado
 * cinza) e, opcional, o cronograma com a etapa de hoje marcada.
 *
 * A situação e as etapas são calculadas no navegador, depois que a página
 * abre: o HTML do servidor (e qualquer cópia em cache) mostra só as datas, e
 * nunca uma situação velha. `agora` fixa o instante (testes).
 */
const PrazoView = ({ data = {}, agora: agoraFixo, vazio = null }) => {
  const [agora, setAgora] = useState(agoraFixo ?? null);
  useEffect(() => {
    if (agoraFixo === undefined) setAgora(Date.now());
  }, [agoraFixo]);

  if (!data.dataFim) return vazio;

  const rotulo = (data.rotulo || '').trim() || 'Inscrições';
  const etapas = (data.etapas || []).filter(
    (e) => e?.inicio && (e.nome || '').trim(),
  );
  const sit = agora !== null ? situacao(data, agora) : null;
  const estados = agora !== null ? estadosDasEtapas(etapas, agora) : [];
  const link = (data.link || '').trim();

  const Folha = ({ iso, hora }) => {
    const f = folha(iso);
    return (
      <div className="ag-prazo__dia">
        <span className="ag-prazo__mes">{f.mes}</span>
        <span className="ag-prazo__num">{f.dia}</span>
        {hora && <span className="ag-prazo__hora">{hora}</span>}
      </div>
    );
  };

  return (
    <section
      className={`ag-prazo ag-prazo--${sit ? sit.estado : 'calculando'}`}
      aria-label={`Prazo: ${rotulo}`}
    >
      <div className="ag-prazo__cartao">
        <div className="ag-prazo__datas" aria-hidden="true">
          {data.dataInicio && data.dataInicio !== data.dataFim && (
            <>
              <Folha
                iso={data.dataInicio}
                hora={horaLegivel(data.horaInicio)}
              />
              <span className="ag-prazo__seta">→</span>
            </>
          )}
          <Folha iso={data.dataFim} hora={horaLegivel(data.horaFim)} />
        </div>
        <div className="ag-prazo__info">
          <span className="ag-prazo__etiqueta">{rotulo}</span>
          <p className="ag-prazo__periodo">{periodo(data)}</p>
          {sit && <p className="ag-prazo__situacao">{sit.frase}</p>}
          {link && (
            <UniversalLink href={link} className="ag-prazo__botao">
              {(data.textoBotao || '').trim() || 'Ver o edital'}
            </UniversalLink>
          )}
        </div>
      </div>
      {etapas.length > 0 && (
        <div className="ag-prazo__cronograma">
          <strong className="ag-prazo__cronograma-titulo">Cronograma</strong>
          <ol className="ag-prazo__etapas">
            {etapas.map((e, i) => {
              const estado = estados[i];
              return (
                <li
                  key={e['@id'] || i}
                  className={`ag-prazo__etapa${estado ? ` ag-prazo__etapa--${estado}` : ''}`}
                >
                  <span>
                    {e.nome.trim()}
                    {estado === 'atual' && (
                      <span className="ag-prazo__agora">agora</span>
                    )}
                  </span>
                  <span className="ag-prazo__etapa-data">
                    {datasDaEtapa(e)}
                  </span>
                </li>
              );
            })}
          </ol>
          {(data.nota || '').trim() && (
            <p className="ag-prazo__nota">{data.nota.trim()}</p>
          )}
        </div>
      )}
    </section>
  );
};

PrazoView.propTypes = {
  data: PropTypes.object,
  agora: PropTypes.number,
  vazio: PropTypes.node,
};

export default PrazoView;
